<div class="page auth_split">
  <app-auth-brand-panel variant="signup" [icp]="icp"></app-auth-brand-panel>

  <div class="form-content">
    <div class="form_topbar">
      <span>Already have an account?</span>
      <a routerLink="/login" class="login-link">Log in</a>
    </div>

    <div class="form-wrapper">
      <!-- The brand panel is hidden on phones, so the matched headline is shown here for them -->
      <div class="mobile_pitch">
        <p class="mobile_pitch_eyebrow" *ngIf="brandCopy.eyebrow">{{ brandCopy.eyebrow }}</p>
        <p class="mobile_pitch_headline">{{ brandCopy.lead }} <span>{{ brandCopy.accent }}</span></p>
      </div>

      <div class="form-title">
        <h2>Start your 14-day free trial</h2>
        <p class="subtitle">14 days of Professional · Up to 5 employees · No credit card</p>
      </div>


      <form class="signup-form" [formGroup]="signupForm" (ngSubmit)="signUpAction()"
        (keydown.enter)="onFormKeyDown($event)" novalidate>
        <div class="stepper-container" [@slideInOut]="step">
          <!-- Step 1: Email -->
          <div *ngIf="step === 1" class="form-step">
            <mat-form-field appearance="outline" class="custom-field" floatLabel="always">
              <mat-label>Email</mat-label>
              <input matInput #emailField formControlName="email" title="Email" placeholder="you@company.com"
                autocomplete="email" inputmode="email" autocapitalize="none" spellcheck="false"
                (keydown.enter)="nextStep()">
              <mat-error
                *ngIf="signupForm.get('email')?.hasError('required') && (signupForm.get('email')?.touched || formSubmitted)">Please enter your email</mat-error>
              <mat-error
                *ngIf="signupForm.get('email')?.hasError('pattern') && (signupForm.get('email')?.touched || formSubmitted)">Please enter a valid email</mat-error>
              <mat-error
                *ngIf="signupForm.get('email')?.hasError('disposableEmail') && (signupForm.get('email')?.touched || formSubmitted)">Please
                use a work email. Temporary inboxes like this cannot be used to create an account.</mat-error>
              <mat-error *ngIf="signupForm.get('email')?.hasError('emailExists')">This email is already registered. <a routerLink="/login" class="inline_link">Log in instead</a></mat-error>
            </mat-form-field>
          </div>

          <!-- Step 2: Full Name & Password -->
          <div *ngIf="step === 2" class="form-step">
            <p class="signing_as">
              <span class="signing_as_email">{{ signupForm.get('email')?.value }}</span>
              <button type="button" class="change_email" (click)="prevStep()">Change</button>
            </p>
            <mat-form-field appearance="outline" class="custom-field" floatLabel="always">
              <mat-label>Full Name</mat-label>
              <input matInput #nameField formControlName="fullName" title="Full Name" placeholder="Enter your full name"
                autocomplete="name">
              <mat-error
                *ngIf="signupForm.get('fullName')?.hasError('required') && (signupForm.get('fullName')?.touched || formSubmitted)">Please enter your full name</mat-error>
              <mat-error
                *ngIf="signupForm.get('fullName')?.hasError('pattern') && (signupForm.get('fullName')?.touched || formSubmitted)">Please
                enter both your first and last name, separated by a space. Don't use numbers or special
                characters.</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="custom-field custom-password-field" floatLabel="always">
              <mat-label>Password</mat-label>
              <input matInput formControlName="password" [attr.type]="passwordVisible ? 'text' : 'password'"
                title="Password should be 8 characters long, with 1 number, 1 special character, and 1 uppercase letter"
                placeholder="Enter your password" autocomplete="new-password"
                (input)="signupForm.get('password')?.markAsTouched()">
              <button mat-icon-button matSuffix (click)="togglePasswordVisibility(); $event.stopPropagation();" type="button" aria-label="Toggle password visibility">
                <mat-icon>{{passwordVisible ? 'visibility' : 'visibility_off'}}</mat-icon>
              </button>
              <mat-error
                *ngIf="signupForm.get('password')?.hasError('required') && (signupForm.get('password')?.touched || formSubmitted)">Please
                create a password</mat-error>
            </mat-form-field>

            <!-- Password Strength Meter -->
            <div *ngIf="signupForm.get('password')?.value" class="password-strength-meter">
              <div class="strength-header">
                <label class="strength-title">Password Strength</label>
                <div class="strength-status-container">
                  <span *ngIf="hasUnmetRequirements" class="strength-status-badge status-invalid">
                    <mat-icon class="status-icon">error</mat-icon>
                    <span>Invalid</span>
                  </span>
                  <span *ngIf="!hasUnmetRequirements" class="strength-status-badge" [ngClass]="'status-' + passwordStrength">
                    <mat-icon class="status-icon" *ngIf="passwordStrength === 'weak'">bolt</mat-icon>
                    <mat-icon class="status-icon" *ngIf="passwordStrength === 'medium'">bolt</mat-icon>
                    <mat-icon class="status-icon" *ngIf="passwordStrength === 'strong'">lock</mat-icon>
                    <mat-icon class="status-icon" *ngIf="passwordStrength === 'very-strong'">verified</mat-icon>
                    <span *ngIf="passwordStrength === 'weak'">Fair</span>
                    <span *ngIf="passwordStrength === 'medium'">Good</span>
                    <span *ngIf="passwordStrength === 'strong'">Strong</span>
                    <span *ngIf="passwordStrength === 'very-strong'">Excellent</span>
                  </span>
                  <!-- Password Breach Status in Header -->
                  <span *ngIf="hasMinLength" class="breach-status-badge">
                    <span *ngIf="isCheckingBreach" class="breach-badge checking">
                      <mat-icon class="breach-icon-small checking">sync</mat-icon>
                    </span>
                    <span *ngIf="!isCheckingBreach && isPasswordBreached" class="breach-badge warning" matTooltip="This password has been found in data breaches. Please choose a different password.">
                      <mat-icon class="breach-icon-small warning">warning</mat-icon>
                    </span>
                    <span *ngIf="!isCheckingBreach && !isPasswordBreached" class="breach-badge safe" matTooltip="Password not found in known data breaches">
                      <mat-icon class="breach-icon-small safe">check_circle</mat-icon>
                    </span>
                  </span>
                </div>
              </div>
              
              <div class="strength-indicator">
                <div class="strength-bar" 
                     [ngClass]="hasUnmetRequirements ? 'strength-invalid' : 'strength-' + passwordStrength"
                     [style.width.%]="requirementsProgress"></div>
              </div>
              
              <!-- Show all password requirements and hints as bullet points -->
              <div *ngIf="unmetPasswordRequirements.length > 0" class="password-feedback">
                <ul class="requirement-list">
                  <li *ngFor="let requirement of unmetPasswordRequirements" class="requirement-item error">
                    <mat-icon class="item-icon">lightbulb</mat-icon>
                    <span class="item-text">{{requirement.text}}</span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Terms and Conditions Checkbox -->
            <div class="terms-checkbox-container">
              <mat-checkbox formControlName="termsAccepted" class="terms-checkbox">
                <span class="terms-text">
                  I agree to the 
                  <span class="terms-link" (click)="openTermsAndConditions($event)">Terms & Conditions</span>
                </span>
              </mat-checkbox>
              <div class="terms-error" *ngIf="signupForm.get('termsAccepted')?.hasError('required') && (signupForm.get('termsAccepted')?.touched || submitAttempted)">
                Please agree to the Terms and Conditions to continue.
              </div>
            </div>
          </div>
        </div>

        <!-- Stepper Navigation -->
        <div class="stepper-navigation" *ngIf="step === 1">
          <button mat-raised-button *ngIf="step === 1" (click)="nextStep()" type="button" class="stepper-btn next-btn"
            color="primary">
            Continue
            <mat-icon>arrow_forward</mat-icon>
          </button>
        </div>

        <!-- Google is the shortcut, not the main path (Clarity: email is used about twice as often), and only makes sense before an email is chosen -->
        <div *ngIf="step === 1" class="alt_signin">
          <div class="separator">
            <span>or</span>
          </div>

          <div class="google-signin-container">
            <app-google-signin (onSigninSuccess)="handleGoogleSigninSuccess($event)"></app-google-signin>
          </div>
        </div>

        <div *ngIf="step === 1">
          <p class="legal_note">
            By continuing, you agree to our
            <a href="https://supersee.io/terms-conditions/" target="_blank" rel="noopener">Terms</a>,
            <a href="https://supersee.io/privacy-policy/" target="_blank" rel="noopener">Privacy Policy</a> and
            <a href="https://supersee.io/legal-dpa/" target="_blank" rel="noopener">DPA</a>.
          </p>
          <!-- The brand panel is hidden on phones, so the privacy promise is repeated here for them -->
          <p class="privacy_note">On your phone? Create your account now. After signing up you can email yourself a link to continue setup on a computer.</p>
        </div>

        <p class="form_submit_error" role="alert" *ngIf="step === 2 && submitAttempted && signupForm.invalid">
          Please complete the fields above to continue.</p>

        <!-- Create Account Button -->
        <button mat-raised-button color="accent" class="signUpBtn" 
          [disabled]="isDisable"
          type="submit" *ngIf="step === 2">
          <mat-spinner *ngIf="isDisable" [diameter]="20"></mat-spinner>
          <span *ngIf="!isDisable">Start free trial</span>
        </button>

      </form>
      <div style="text-align: center; margin-top: 20px;">
        <p>Need Help? <a href="https://wa.me/916354719703?text=Hi, I need some help with SuperSee" target="_blank"
            rel="noopener" style="color: rgb(0, 0, 0, 0.7); text-decoration: none;">Chat
            on WhatsApp</a></p>
      </div>
    </div>
  </div>
</div>